import { useState } from "react";
import { Link } from "react-router-dom";
import { Footprints, LogOut, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { formatDateTimeAR, formatTimeAR } from "@/lib/i18n";

const TYPE_LABEL = { persona: "Persona", proveedor: "Proveedor", delivery: "Delivery", transporte: "Transporte", correo: "Correo" };

// Hora de entrada; si fue otro día (ventana de 24 h), con fecha.
const entradaLabel = (iso) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? formatTimeAR(iso) : formatDateTimeAR(iso);
};

const peopleOf = (data) => (data?.clients || []).flatMap((c) => c.people.map((p) => ({ ...p, client_name: c.client_name })));

/** Fix BN (2026-11-03): salida en un toque. El backend copia los datos del ingreso. */
async function registerExit(p, onChanged) {
  try {
    await api.post(`/visits/${p.visit_id}/exit`);
    toast.success(`Salida registrada: ${p.name} ${p.surname}`);
  } catch (e) { toast.error(formatApiError(e)); }
  if (onChanged) await onChanged();
}

/** Panel "Visitas activas" del Inicio del vigilador: personas adentro, últimas 24 h. GET /api/visits/active. */
export function ActiveVisitsPanel({ data, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  if (!data) return null;
  const { total_inside, clients } = data;
  const people = peopleOf(data);
  const exit = async (p) => { setBusyId(p.visit_id); await registerExit(p, onChanged); setBusyId(null); };

  return (
    <div className="card" data-testid="active-visits-panel"
      style={{ padding: 18, marginBottom: 14, background: "#0A0A0A", color: "#FFFBF4", borderRadius: 4, border: "1px solid rgba(191,52,55,0.35)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase" }}>Visitas activas</div>
          <div style={{ fontSize: 34, fontWeight: 900, lineHeight: 1, marginTop: 4 }} data-testid="active-visits-count">{total_inside}</div>
          <div style={{ fontSize: 11, color: "rgba(255,251,244,0.6)" }}>
            {total_inside === 1 ? "persona adentro · falta salir" : "personas adentro · faltan salir"}
          </div>
        </div>
        <Footprints size={28} style={{ color: "#BF3437", flexShrink: 0 }} />
      </div>

      {people.length > 0 ? (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
          {people.slice(0, 4).map((p) => (
            <div key={p.visit_id || p.dni} data-testid={`active-visit-person-${p.dni}`}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 12, borderTop: "1px solid rgba(255,251,244,0.08)", paddingTop: 6 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                {p.name} {p.surname}{p.companions_count > 0 ? ` +${p.companions_count}` : ""}
                {clients.length > 1 && <span style={{ color: "rgba(255,251,244,0.4)" }}> · {p.client_name}</span>}
              </span>
              <span style={{ color: "rgba(255,251,244,0.5)", flexShrink: 0 }}>{entradaLabel(p.entrada_at)}</span>
              {p.visit_id && (
                <button type="button" onClick={() => exit(p)} disabled={busyId === p.visit_id} data-testid={`active-visit-exit-${p.visit_id}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                    border: "1px solid #BF3437", background: "transparent", color: "#FFFBF4", borderRadius: 4, cursor: "pointer", flexShrink: 0 }}>
                  {busyId === p.visit_id ? <Loader2 size={11} className="spin" /> : <LogOut size={11} />} Salida
                </button>
              )}
            </div>
          ))}
          {people.length > 4 && (
            <Link to="/vigilador/visitas" style={{ fontSize: 11, color: "rgba(255,251,244,0.7)", fontWeight: 700 }} data-testid="active-visits-more">
              +{people.length - 4} más · ver todos
            </Link>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,251,244,0.45)" }}>Todos salieron. Ninguna visita pendiente.</div>
      )}

      <Link to="/vigilador/visitas" className="btn-primary" data-testid="active-visits-cta"
        style={{ width: "100%", marginTop: 14, textAlign: "center", display: "block", textDecoration: "none", padding: "12px", fontSize: 12 }}>
        Registrar ingreso / egreso
      </Link>
    </div>
  );
}

/** Fix BN (2026-11-03): lista "Adentro ahora" de Visitas, con salida en un toque. */
export function InsideNowList({ data, onChanged, currentUserId }) {
  const [onlyMine, setOnlyMine] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const people = peopleOf(data).filter((p) => p.visit_id);
  const shown = onlyMine ? people.filter((p) => p.user_id === currentUserId) : people;
  const exit = async (p) => { setBusyId(p.visit_id); await registerExit(p, onChanged); setBusyId(null); };

  return (
    <div className="card" style={{ padding: 0, marginBottom: 18, overflow: "hidden" }} data-testid="visit-inside-list">
      <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", borderBottom: "1px solid var(--invict-bone-3)" }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A" }} data-testid="visit-inside-count">
          Adentro ahora · {shown.length}
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} data-testid="visit-inside-only-mine" />
          Solo los que registré yo
        </label>
      </div>
      {shown.length === 0 && (
        <div style={{ padding: 18, textAlign: "center", fontSize: 12, color: "var(--invict-text-muted)" }}>
          Nadie adentro{onlyMine ? " de los que registraste" : ""}.
        </div>
      )}
      {shown.map((p) => (
        <div key={p.visit_id} style={{ padding: "10px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", alignItems: "center", gap: 10 }} data-testid={`visit-inside-row-${p.visit_id}`}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>
              {p.surname}, {p.name}{p.companions_count > 0 ? ` +${p.companions_count}` : ""}
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "var(--invict-text-muted)" }}>{TYPE_LABEL[p.visit_type] || "Persona"}</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
              DNI {p.dni} · {p.client_name}{p.site_name ? ` · ${p.site_name}` : ""} · entró {entradaLabel(p.entrada_at)}
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
              {p.company ? `Empresa: ${p.company} · ` : ""}{p.vehicle_plate ? `Patente ${p.vehicle_plate} · ` : ""}Registró: {p.user_name || "—"}
            </div>
          </div>
          <button type="button" className="btn-primary" onClick={() => exit(p)} disabled={busyId === p.visit_id} data-testid={`visit-exit-${p.visit_id}`}
            style={{ padding: "10px 14px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {busyId === p.visit_id ? <Loader2 size={14} className="spin" /> : <LogOut size={14} />} Registrar salida
          </button>
        </div>
      ))}
    </div>
  );
}
